import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import CoachProfileDialog from "./CoachProfileDialog";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@mui/styles";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import Badge from "@mui/material/Badge";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FlareIcon from "@mui/icons-material/Flare";
import ClearIcon from "@mui/icons-material/Clear";
import Avatar from "@mui/material/Avatar";
import Loader from "../../sharedComponents/Loader/Loader";
import RouteConstants from "../../common/RouteConstants";
import AppConstants from "../../common/AppConstants";
import CardContainer from "../dashbord/CardContainer";
import ListContainer from "../dashbord/ListContainer";
import FrontOfficeService from "../../services/frontOffice.service";
import { userService, coachProfileService } from "../../services";
import { AppDataContext } from "../../common/AppContext";
import CreateGroupDialog from "../../sharedComponents/dialog/CreateGroupDialog";
import DiscardGroupIcon from "../../sharedComponents/customIcons/DiscardGroup";
import CreateGroupIcon from "../../sharedComponents/customIcons/CreateGroupIcon";
import DownloadIcon from "../../sharedComponents/customIcons/DownloadIcon";
import CreateShowcaseIcon from "../../sharedComponents/customIcons/CreateShowcaseIcon";
import clsx from "clsx";
import {
  DashBoardProvider,
  DashBoardConsumer,
  DashBoardContext,
} from "../../common/DashBoardContext";
import { FilterContext } from "../../common/FilterContext";
import Pagination from "../../sharedComponents/Pagination/Pagination";
import ProfileCommonFunction from "../../sharedComponents/ProfileCard/ProfileCommonFunction";
import RejectDialog from "../../sharedComponents/dialog/RejectDialog";
import AdminService from "../../services/admin.service";
import AdminCoachesAppBar from "./AdminCoachesAppBar";
import AddShowcaseParticipantsDialog from "./AddShowcaseParticipantsDialog";
import AdminCoachesTabPanel from "./AdminCoachesTabPanel";
import FrontOfficeCoachesAppBar from "./FrontOfficeCoachesAppBar";
import FrontOfficeCoachesTabPanel from "./FrontOfficeCoachesTabPanel";
import AdvancedFilterDialog from "./AdvancedFilterDialog";
import arrow_toggle_transparent from "../../images/arrow_toggle_transparent.png";
import { Role } from "../../helper";
import SnackbarMsg from "../../sharedComponents/Snackbar/Snackbar";
import adminService from "../../services/admin.service";
import useTelemetry from "../../utils/useTelemetry";
import useRotatingPlaceholder from "../../hooks/useRotatingPlaceholder";
import "../../styles/RotatingPlaceholder.css";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  content: {
    width: "100%",
    margin: theme.spacing(2),
    boxShadow: "0px 0px 5px 3px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    zIndex: '2',
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(5, 0, 2, 0),
      width: "100%",
    },
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid rgba(0,0,0,0.1)',
  },
  searchInput: {
    maxWidth: 820,
    width: '100%',
  },
  clearAllLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(1),
  },
  tabsBar: {
    justifyContent: 'space-evenly'
  },
  tab: {
    fontSize: theme.typography.pxToRem(12),
  },
  toolbar: {
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  marginLeft: {
    marginLeft: '10px',
  },
  sorting: {
    '& > div': {
      marginLeft: theme.spacing(1),
      '& > svg': {
        color: '#ffffff',
      },
      '& > div': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default + ' !important' : theme.palette.primary.main + ' !important',
        borderRadius: '12px !important',
        color: '#ffffff',
        padding: '8px 12px !important'
      },
    }
  },
  Pagination: {
    '& > div': {
      marginLeft: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        marginLeft: theme.spacing(0),
      },
      '& > svg': {
        color: '#ffffff',
      },
      '& > div': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default + ' !important' : theme.palette.primary.main + ' !important',
        borderRadius: '12px !important',
        color: '#ffffff',
        padding: '8px 12px !important'
      },
    },
  },
  sort: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  sortBy: {
    [theme.breakpoints.up("sm")]: {
      margin: theme.spacing(1),
      width: "25ch",
    },
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      width: "18ch",
    },
  },
  viewAs: {
    minWidth: "fit-content",
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  tabsColor: {
    '& button': {
      color: '#ffffff !important',
    },
  },
  iconAvatar2: {
    width: theme.spacing(1.75),
    height: theme.spacing(2.25),
  },
  iconAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  iconGroup: {
    borderRight: '1px solid #999',
    '& .MuiIconButton-root': {
      color: '#000000',
    },
    '& .MuiIconButton-root.MuiIconButton-colorSecondary': {
      color: '#c8102e',
    },
  }
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`scroll-tabpanel-${index}`}
      aria-labelledby={`scroll-tab-${index}`}
      {...other}
    >
      {value === index && <div p={3}>{children}</div>}
    </Typography>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `scroll-tab-${index}`,
    "aria-controls": `scroll-tabpanel-${index}`,
  };
}

function filterReducer(state, action) {
  switch (action.type) {
    case 'TOGGLE_ITEM': {
      const list = state[action.field];
      const exists = list.includes(action.value);
      return {
        ...state,
        [action.field]: exists
          ? list.filter(v => v !== action.value)
          : [...list, action.value],
      };
    }
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'RESET':
      return {
        searchedText: '',
        useSmartSearch: false,
        selectedTitle: [],
        selectedLeague: [],
        selectedChampionship: [],
        selectedSkill: [],
        selectedTeams: [],
        selectedNBATeams: [],
        selectedWNBATeams: [],
        selectedNBAGLeagueTeams: [],
        selectedCurrentlyWithATeam: [],
        selectedFormerCoach: [],
        selectedExpType: [],
        selectedPExpereience: [],
        internationalWorked: false,
      };
    default:
      return state;
  }
}

const CoachesManagementContainer = (props) => {
  const classes = useStyles();
  const theme = useTheme();

  const [view, setView] = useState("card");
  const [role, setRole] = useState("");
  const [foTabs, setFOTabs] = useState([]);
  const { trackCustomEvents } = useTelemetry();
  const [smartSearchEnabled, setSmartSearchEnabled] = useState(true);

  // Placeholder suggestions for rotating search hints - conditional based on search mode
  // useMemo ensures the array reference only changes when smartSearchEnabled changes,
  // preventing the hook from resetting on every render.
  const placeholderSuggestions = useMemo(() => smartSearchEnabled ? [
    "Coach who has current head coach of Atlanta Hawks team.",
    "Coach who has worked with WNBA team.",
    "Coach who has past Brooklyn Nets team assistant coach experience.",
    "Coaches with more than 10 years of coaching experience.",
    "Coaches with less than 2 years of experience.",
    "Player development coach with head coach experience.",
    "Coaches with master degrees.",
    "Coaches who speak multiple languages.",
    "Offensive coordinators, 10 years of experience."
  ] : [
    "Enter coach or player name"
  ], [smartSearchEnabled]);

  useEffect(() => {
    getFrontOfficeTabs()
  }, [])

  async function getFrontOfficeTabs() {
    let tabs = await adminService.getFrontOfficeTabs();
    setFOTabs(tabs);
  }

  useEffect(() => {
    setRole(userService.getUserRole());
  }, [])

  // Clear search text when toggling between smart search and advanced filters
  useEffect(() => {
    dispatchFilter({ type: 'SET_FIELD', field: 'searchedText', value: '' });
  }, [smartSearchEnabled])

  const {
    profileList,
    totalRecords,
    setTotalRecords,
    jwtTokenDetails,
    updateBookmarkedUser,
    error,
    isProcessing,
    updateStatusProfile,
    currentPageForUpdate,
    randomKey,
    updateCurrentPage,
    handleServiceCall,
    rules
  } = useContext(DashBoardContext);

  const {
    searchedTextContext,
    selectedTitleContext,
    selectedLeagueContext,
    selectedChampionshipContext,
    selectedSkillContext,
    selectSortContext,
    sortOrderContext,
    filterViewContext,
    selectedTeamsContext,
    selectedNBATeamsContext,
    selectedWNBATeamsContext,
    selectedNBAGLeagueTeamsContext,
    selectedCurrentlyWithATeamContext,
    selectedFormerCoachesContext,
    titlesearchedTextContext,
    leaguesearchedTextContext,
    championshipsearchedTextContext,
    skillsearchedTextContext,
    currentlyWithATeamsearchedTextContext,
    formerCoachessearchedTextContext,
    teamsearchedTextContext,
    nbaTeamsearchedTextContext,
    wnbaTeamsearchedTextContext,
    nbaGLeagueTeamsearchedTextContext,
    filterCriteriaContext,
    internationalContext,
    selectedExpTypeContext,
    pageSizeContext,
    currentPageContext,
    tabIndexContext,
    updateAllSearchedState, 
    updateGroup, userGroup, 
    selectedPExpereienceContext, pExpereienceTextContext,
    useSmartSearchContext
   } = useContext(FilterContext);



  const { teamList, getTeamsByLeague, getLeagueTextByValue, positionTitle, coachTitle, skills, league } = useContext(AppDataContext);
  const [nbaTeamsList, setNbaTeamList] = useState([]);
  const [WnbaTeamsList, setWnbaTeamList] = useState([]);
  const [nbaGLeagueTeamsList, setNbaGLeagueTeamList] = useState([]);




  useEffect(() => {
    let list = [];
    getTeamsByLeague("nba").forEach(element => {
      list.push({ Id: element.teamId, Name: element.teamName })
    });
    setNbaTeamList(list);
    list = [];
    getTeamsByLeague("wnba").forEach(element => {
      list.push({ Id: element.teamId, Name: element.teamName })
    });
    setWnbaTeamList(list);
    list = [];
    getTeamsByLeague("nbagleague").forEach(element => {
      list.push({ Id: element.teamId, Name: element.teamName })
    });
    setNbaGLeagueTeamList(list);

  }, [teamList]);

  const [positionTitleList, setPositionTitleList] = useState([]);
  useEffect(() => {
    let list = [{ Id: "4", Name: "Head Coach" }
      , { Id: "1", Name: "Assistant Coach" }
      , { Id: "6", Name: "Other" }
      , { Id: "2", Name: "Associate Head Coach" }
      , { Id: "3", Name: "Front of the Bench Assistant Coach" }
      , { Id: "5", Name: "Player Development Coach" }]


    list = ProfileCommonFunction.sortById(list, "Id");
    setPositionTitleList(list);
  }, [positionTitle, coachTitle])

  const [tabIndex, setTabIndex] = useState(tabIndexContext);

  const [filterState, dispatchFilter] = useReducer(filterReducer, {
    searchedText: searchedTextContext || '',
    useSmartSearch: useSmartSearchContext === true ? true : false,
    selectedTitle: selectedTitleContext || [],
    selectedLeague: selectedLeagueContext || [],
    selectedChampionship: selectedChampionshipContext || [],
    selectedSkill: selectedSkillContext || [],
    selectedTeams: selectedTeamsContext || [],
    selectedNBATeams: selectedNBATeamsContext || [],
    selectedWNBATeams: selectedWNBATeamsContext || [],
    selectedNBAGLeagueTeams: selectedNBAGLeagueTeamsContext || [],
    selectedCurrentlyWithATeam: selectedCurrentlyWithATeamContext || [],
    selectedFormerCoach: selectedFormerCoachesContext || [],
    selectedExpType: selectedExpTypeContext || [],
    selectedPExpereience: selectedPExpereienceContext || [],
    internationalWorked: internationalContext || false,
  });

  const {
    searchedText, useSmartSearch,
    selectedTitle, selectedLeague, selectedChampionship, selectedSkill,
    selectedTeams, selectedNBATeams, selectedWNBATeams, selectedNBAGLeagueTeams,
    selectedCurrentlyWithATeam, selectedFormerCoach, selectedExpType,
    selectedPExpereience, internationalWorked,
  } = filterState;

  // Memoize the mapped array so its reference only changes when the mode toggles,
  // preventing the hook from treating every render as a new suggestions list.
  const rotatingPlaceholderSuggestions = useMemo(
    () => placeholderSuggestions.map(p => `Hint: ${p}`),
    [placeholderSuggestions]
  );
  // Pause rotation once the user starts typing
  const rotatingPlaceholder = useRotatingPlaceholder(rotatingPlaceholderSuggestions, 4000, !!searchedText);

  const [filterTexts, setFilterTexts] = useState({
    title: titlesearchedTextContext || '',
    league: leaguesearchedTextContext || '',
    championship: championshipsearchedTextContext || '',
    skill: skillsearchedTextContext || '',
    currentWork: currentlyWithATeamsearchedTextContext || '',
    formerCoaches: formerCoachessearchedTextContext || '',
    team: teamsearchedTextContext || '',
    nbaTeams: nbaTeamsearchedTextContext || '',
    wnbaTeams: wnbaTeamsearchedTextContext || '',
    nbaGLeagueTeams: nbaGLeagueTeamsearchedTextContext || '',
    pExperience: pExpereienceTextContext || '',
  });

  const {
    title: titlesearchedText, league: leaguesearchedText,
    championship: championshipsearchedText, skill: skillsearchedText,
    currentWork: currentlyWithATeamsearchedText, formerCoaches: formerCoachessearchedText,
    team: teamsearchedText, nbaTeams: nbaTeamsearchedText,
    wnbaTeams: wnbaTeamsearchedText, nbaGLeagueTeams: nbaGLeagueTeamsearchedText,
    pExperience: pExperiencesearchedText,
  } = filterTexts;

  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const currentUserName = userService.getUsername();
  const [filteredProfileList, setFilterProfile] = useState([]);
  const [filterView, setFilterView] = useState(filterViewContext);
  //const [profileList, setProfileList] = useState([]);
  const [selectSort, setSelectSort] = useState(selectSortContext);
  const [sortOrder, setSortOrder] = useState(sortOrderContext);
  const [currentPage, setCurrentPage] = useState(currentPageContext);
  const [randomKeyForUpdate, setRandomKeyForUpdate] = useState(randomKey);
  const [filterCriteria, setFilterCriteria] = useState(filterCriteriaContext);
  const [pageSize, setPageSize] = useState(pageSizeContext);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [showGroupButton, setShowGroupButton] = useState(false);
  const [showShowcase, setShowShowcase] = useState(false);
  const [showDownload, setShowDownload] = useState(false);

  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");
  const [clearSearch, setClearSearch] = useState(false);
  const [openParticipantDialog, toggleParticipantDialog] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [action, setAction] = useState();
  const [group, setGroup] = useState(userGroup);
  const [showGroupTab, setShowGroupTab] = useState(false);
  const [groupUsers, setGroupUsers] = useState([]);
  const [isGroupProfile, setIsGroupProfile] = useState(false);
  const [groupPageNo, setGroupPageNo] = useState(1);
  const [groupPageSize, setGroupPageSize] = useState(12);
  const [totalRecordGroup, setTotalRecordGroup] = useState(12);
  //selectedPExpereienceContext, pExpereienceTextContext
  
  useEffect(() => {
    setIsLoading(true);
    setRandomKeyForUpdate(randomKey);
    if (userService.getUserRole() == Role.Admin)
      getAllGroups();
  }, [randomKey]);



  const getAllGroups = () => {
    adminService.getAllGroups()
      .then(result => {
        if (result && result.length > 0) {
          var lstGrp = result;
          lstGrp = result.filter((g) => g.isFrontOfficeGroup && g.isFrontOfficeGroup == 1)
          setAllGroups(lstGrp);
        }
        setLoading(true);
      }).catch((err) => {
      });
  }
  useEffect(() => {
    updateGroup(undefined);
    return;
  })
  useEffect(() => {
    if (!isProcessing) {
      setIsLoading(false);
    }
  }, [randomKeyForUpdate]);

  // useEffect(()=>{
  //   updateCurrentPage(currentPageContext);
  // },[currentPageContext])


  useEffect(() => {
    updateAllSearchedState(
      filterState.searchedText,
      filterState.selectedTitle,
      filterState.selectedLeague,
      filterState.selectedChampionship,
      filterState.selectedSkill,
      selectSort,
      sortOrder,
      filterView,
      filterState.selectedTeams,
      filterState.selectedNBATeams,
      filterState.selectedWNBATeams,
      filterState.selectedNBAGLeagueTeams,
      filterState.selectedCurrentlyWithATeam,
      filterState.selectedFormerCoach,
      filterTexts.title,
      filterTexts.league,
      filterTexts.championship,
      filterTexts.skill,
      filterTexts.currentWork,
      filterTexts.formerCoaches,
      filterTexts.team,
      filterTexts.nbaTeams,
      filterTexts.wnbaTeams,
      filterTexts.nbaGLeagueTeams,
      filterCriteria,
      pageSize, currentPage, tabIndex, filterState.internationalWorked,
      filterState.selectedExpType, filterState.selectedPExpereience,
      filterTexts.pExperience, filterState.useSmartSearch)
  }, [filterState, filterTexts, selectSort, sortOrder, filterView, filterCriteria,
    pageSize, currentPage, tabIndex]);



  useEffect(() => {
    if (!isProcessing) {
      //hack to show loader till details are fetched..
      setIsLoading(false);
      setFilterProfile(profileList);
      //  handleFilterSearchClick();
    }
  }, [profileList, isProcessing]);

  useEffect(() => {
    if (error && error !== "") {
      setIsLoading(false);
      //console.log(error);
    }
  }, [error]);

  const handleProfileChecked = (e, userId) => {
    let selected = [...selectedProfiles];
    if (e.target.checked) selected.push(userId);
    else {
      let index = selected.indexOf(userId);
      selected.splice(index, 1);
    }
    setSelectedProfiles(selected);
    let currentRole = userService.getUserRole();
    if (currentRole == Role.Admin) {
      if (selected.length > 0 && group == undefined) {
        setShowShowcase(true);
        setShowGroupButton(true);
        setShowDownload(true);
      }
      else if (selected.length > 0 && group) {
        setShowShowcase(false);
        setShowGroupButton(true);
        setShowDownload(false);
      }
      else if ((selected.length == 0 && showGroupButton == true))
        setShowGroupButton(true);
      else {
        setShowShowcase(false);
        setShowGroupButton(false);
        setShowDownload(false);
      }
    }
    else {
      if (selected.length > 0) {
        setShowDownload(true);
      }
    }
  }
  const handleChecked = (e, name, item, searchText) => {
    const filterFieldMap = {
      championship: { field: 'selectedChampionship', textKey: 'championship', getValue: (i) => i },
      league: { field: 'selectedLeague', textKey: 'league', getValue: (i) => i },
      team: { field: 'selectedTeams', textKey: 'team', getValue: (i) => i.Name },
      nbaTeams: { field: 'selectedNBATeams', textKey: 'nbaTeams', getValue: (i) => i.Name },
      wnbaTeams: { field: 'selectedWNBATeams', textKey: 'wnbaTeams', getValue: (i) => i.Name },
      nbaGLeagueTeams: { field: 'selectedNBAGLeagueTeams', textKey: 'nbaGLeagueTeams', getValue: (i) => i.Name },
      positionTitle: { field: 'selectedTitle', textKey: 'title', getValue: (i) => i.Name },
      skill: { field: 'selectedSkill', textKey: 'skill', getValue: (i) => i.Name },
      currentWork: { field: 'selectedCurrentlyWithATeam', textKey: 'currentWork', getValue: (i) => i.Name },
      formerCoaches: { field: 'selectedFormerCoach', textKey: 'formerCoaches', getValue: (i) => i.Name },
      expType: { field: 'selectedExpType', textKey: null, getValue: (i) => i.Name },
      playingExperience: { field: 'selectedPExpereience', textKey: 'pExperience', getValue: (i) => i.Name },
    };

    if (name === 'international') {
      dispatchFilter({ type: 'SET_FIELD', field: 'internationalWorked', value: e.target.checked });
      return;
    }

    const mapping = filterFieldMap[name];
    if (!mapping) return;

    if (mapping.textKey && searchText !== undefined) {
      setFilterTexts(prev => ({ ...prev, [mapping.textKey]: searchText }));
    }
    dispatchFilter({ type: 'TOGGLE_ITEM', field: mapping.field, value: mapping.getValue(item) });
  };

  useEffect(() => {
    let expType = [];
    if (filterState.selectedCurrentlyWithATeam && filterState.selectedCurrentlyWithATeam.length > 0) {
      expType.push("Current")
    }
    if (filterState.selectedFormerCoach && filterState.selectedFormerCoach.length > 0) {
      expType.push("Past")
    }
    dispatchFilter({ type: 'SET_FIELD', field: 'selectedExpType', value: expType });
  }, [filterState.selectedFormerCoach, filterState.selectedCurrentlyWithATeam])

  const handleChipDelete = (name, item) => {
    const chipFieldMap = {
      championship: 'selectedChampionship',
      league: 'selectedLeague',
      positionTitle: 'selectedTitle',
      skill: 'selectedSkill',
      currentWork: 'selectedCurrentlyWithATeam',
      formerCoaches: 'selectedFormerCoach',
      expType: 'selectedExpType',
      teams: 'selectedTeams',
      nbaTeams: 'selectedNBATeams',
      wnbaTeams: 'selectedWNBATeams',
      nbaGLeagueTeams: 'selectedNBAGLeagueTeams',
      playingExperience: 'selectedPExpereience',
    };

    if (name === 'international') {
      dispatchFilter({ type: 'SET_FIELD', field: 'internationalWorked', value: false });
      return;
    }

    const field = chipFieldMap[name];
    if (field) {
      dispatchFilter({ type: 'TOGGLE_ITEM', field, value: item });
    }
  };

  const handleClearAllFilter = () => {
    setCurrentPage(1);
    setFilterProfile([]);
    setIsLoading(true);
    setSearchExecuted(false);
    setAppliedFilterCount(0);
    dispatchFilter({ type: 'RESET' });
    setFilterTexts({
      title: '', league: '', championship: '', skill: '',
      currentWork: '', formerCoaches: '', team: '',
      nbaTeams: '', wnbaTeams: '', nbaGLeagueTeams: '', pExperience: '',
    });
    clearAllFilter();
  };

  const onWithinFilterSearchedTextchange = (name, text) => {
    const keyMap = {
      positionTitle: 'title', league: 'league', skill: 'skill',
      currentWork: 'currentWork', formerCoaches: 'formerCoaches',
      championship: 'championship', team: 'team',
      nbaTeams: 'nbaTeams', wnbaTeams: 'wnbaTeams', nbaGLeagueTeams: 'nbaGLeagueTeams',
    };
    const key = keyMap[name];
    if (key) setFilterTexts(prev => ({ ...prev, [key]: text }));
  };

  const handleChangeAdmin = (event, newValue) => {
    setCurrentPage(1);
    setTabIndex(newValue);
    setIsGroupProfile(false);
    //setFilterProfile([]);
    //  setIsLoading(true);
    if (newValue == 0) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.All);
      trackCustomEvents("ClickEvent", { event: "All Profiles" });
    }

    if (newValue == 1) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.RecentlyUpdated);
      trackCustomEvents("ClickEvent", { event: "Resubmitted" });
    }

    if (newValue == 2) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.RecentlyAdded);
      trackCustomEvents("ClickEvent", { event: "Recently Added" });
    }

    if (newValue == 3) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.Pending);
      trackCustomEvents("ClickEvent", { event: "Pending Approvals" });
    }

    if (newValue == 4) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.Approved);
      trackCustomEvents("ClickEvent", { event: "Approved Profiles" });
    }

    if (newValue == 5) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.Rejected)
      trackCustomEvents("ClickEvent", { event: "On Hold Profiles" });
    }

    if (newValue == 6) {
      setIsGroupProfile(true);
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.GroupProfile)
      trackCustomEvents("ClickEvent", { event: "Group Profile" });
    }
    //handleSearchClick();
  };

  const handleChange = (event, newValue) => {
    setIsGroupProfile(false);
    setGroupPageNo(1);
    setCurrentPage(1);
    setTabIndex(newValue);
    //setFilterProfile([]);
    //setIsLoading(true);
    if (newValue == 0) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.All);
      trackCustomEvents("ClickEvent", { event: "All Profiles" });
    }
    else if (newValue == 1) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.Bookmarked);
      trackCustomEvents("ClickEvent", { event: "Bookmarked" });
    }

    else if (newValue == 2) {
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.RecentlyAdded);
      trackCustomEvents("ClickEvent", { event: "Recently Added" });
    }

    else if (newValue > 2) {
      let groupData = foTabs[newValue - 3]
      setGroup(groupData)
      setIsGroupProfile(true);
      ////
    }
  };


  useEffect(() => {
    setIsLoading(true);
    handleServiceCall(
      filterState.searchedText,
      filterState.selectedTitle,
      filterState.selectedLeague,
      filterState.selectedChampionship,
      filterState.selectedSkill,
      selectSort,
      sortOrder,
      filterView,
      filterState.selectedNBATeams,
      filterState.selectedWNBATeams,
      filterState.selectedNBAGLeagueTeams,
      pageSize,
      currentPage,
      filterCriteria,
      filterState.selectedCurrentlyWithATeam,
      filterState.selectedFormerCoach,
      filterState.internationalWorked,
      filterState.selectedExpType,
      filterState.selectedPExpereience,
      filterState.useSmartSearch
    )
  }, [
    filterViewContext,
    //selectedTitleContext,
    //selectedLeagueContext,
    //selectedChampionshipContext,
    //selectedSkillContext,
    selectSortContext,
    sortOrderContext,
    //selectedNBATeamsContext,
    //selectedWNBATeamsContext,
    //selectedNBAGLeagueTeamsContext,
    pageSizeContext,
    currentPage,
    filterCriteria,
    // selectedCurrentlyWithATeam,
    // internationalContext
  ]);

  const onPageNoChange = (pageNo) => {
    setCurrentPage(pageNo);
  }

  const onGroupTabPageNoChange = (pageNo) => {
    setGroupPageNo(pageNo);
  }

  const handleChangeIndex = (index) => {
    setTabIndex(index);
    setCurrentPage(1);
    setFilterView(index);
  };

  function isXsDown() {
    return useMediaQuery(theme.breakpoints.down("xs"));
  }

  useEffect(() => {
    if (clearSearch) {
      handleSearchClick();
      //setShowGroupButton(false);
    }
  }, [clearSearch]);

  const handleBookMarked = (mode, userName) => {
    let postData = { profileUserName: userName, bookmark: mode };
    setIsLoading(true);
    FrontOfficeService.setBookmarkProfile(postData).then((data) => {
      //setFilterProfile([]);
      updateBookmarkedUser(data, currentPage);
      if (groupUsers && groupUsers.length > 0) {
        handleUpdateBookmarkedForGroup(data);
      }
      //setFilterProfile([]);
      // setIsLoading(true);
    });
  };

  const handleUpdateBookmarkedForGroup = (data) => {
    let profile = groupUsers.filter((profile) => {
      if (profile.userName === data.profileUserName) {
        if (data.bookmark) {
          let newData = [];
          profile.isBookMarked = true;
          profile.bookmarkedBy !== null
            ? profile.bookmarkedBy.push(data.bookmarkedBy)
            : (profile.bookmarkedBy = Array.from([data.bookmarkedBy]));
        } else {
          profile.isBookMarked = false;
          profile.bookmarkedBy.splice(
            profile.bookmarkedBy.indexOf(data.bookmarkedBy),
            1
          );
        }
      }
      return profile;
    });
    setGroupUsers(profile);
  };


  const [openDialog, setOpenDialog] = useState({ open: false, data: {} });

  const handleOpenDialogAdmin = (profileObj, showApprove, showReject) => {
    setOpenDialog({ open: true, data: profileObj, showApprove: showApprove, showReject: showReject });
  };

  const handleOpenDialog = (profileObj) => {
    setOpenDialog({ open: true, data: profileObj });
  };

  const handleChangeSortBy = (e) => {
    setCurrentPage(1);
    setSelectSort(e.target.value);
    setFilterProfile([]);
    setIsLoading(true);
  };

  const handleSortOrder = () => {
    setCurrentPage(1);
    setFilterProfile([]);
    setIsLoading(true);
    if (sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("asc");
    }
  };

  const handleSearchClick = () => {
    setCurrentPage(1);
    setFilterProfile([]);
    setIsLoading(true);
    if (showGroupTab !== true)
      setShowGroupButton(true);
    setClearSearch(false);
    handleServiceCall(
      filterState.searchedText,
      filterState.selectedTitle,
      filterState.selectedLeague,
      filterState.selectedChampionship,
      filterState.selectedSkill,
      selectSort,
      sortOrder,
      filterView,
      filterState.selectedNBATeams,
      filterState.selectedWNBATeams,
      filterState.selectedNBAGLeagueTeams,
      pageSize,
      currentPage,
      filterCriteria,
      filterState.selectedCurrentlyWithATeam,
      filterState.selectedFormerCoach,
      filterState.internationalWorked,
      filterState.selectedExpType,
      filterState.selectedPExpereience,
      filterState.useSmartSearch
    )
  };

  const handleMainSearchSubmit = () => {
    dispatchFilter({ type: 'SET_FIELD', field: 'useSmartSearch', value: smartSearchEnabled });
    setSearchExecuted(true);
    setCurrentPage(1);
    setFilterProfile([]);
    setIsLoading(true);
    if (showGroupTab !== true)
      setShowGroupButton(true);
    setClearSearch(false);
    if (smartSearchEnabled) {
      handleServiceCall(
        filterState.searchedText,
        [], [], [], [],
        selectSort, sortOrder, filterView,
        [], [], [],
        pageSize, 1, filterCriteria,
        [], [], false, [], [], true
      );
    } else {
      handleServiceCall(
        filterState.searchedText,
        filterState.selectedTitle,
        filterState.selectedLeague,
        filterState.selectedChampionship,
        filterState.selectedSkill,
        selectSort, sortOrder, filterView,
        filterState.selectedNBATeams,
        filterState.selectedWNBATeams,
        filterState.selectedNBAGLeagueTeams,
        pageSize, 1, filterCriteria,
        filterState.selectedCurrentlyWithATeam,
        filterState.selectedFormerCoach,
        filterState.internationalWorked,
        filterState.selectedExpType,
        filterState.selectedPExpereience,
        false
      );
    }
  };

  const handleAdvancedFilterApply = () => {
    dispatchFilter({ type: 'SET_FIELD', field: 'useSmartSearch', value: false });
    setSearchExecuted(true);
    setAppliedFilterCount(
      filterState.selectedTitle.length +
      filterState.selectedLeague.length +
      filterState.selectedChampionship.length +
      filterState.selectedSkill.length +
      filterState.selectedNBATeams.length +
      filterState.selectedWNBATeams.length +
      filterState.selectedNBAGLeagueTeams.length +
      filterState.selectedCurrentlyWithATeam.length +
      filterState.selectedFormerCoach.length +
      filterState.selectedPExpereience.length +
      (filterState.internationalWorked ? 1 : 0)
    );
    setAdvancedFilterOpen(false);
    setCurrentPage(1);
    setFilterProfile([]);
    setIsLoading(true);
    if (showGroupTab !== true)
      setShowGroupButton(true);
    setClearSearch(false);
    handleServiceCall(
      filterState.searchedText,
      filterState.selectedTitle,
      filterState.selectedLeague,
      filterState.selectedChampionship,
      filterState.selectedSkill,
      selectSort,
      sortOrder,
      filterView,
      filterState.selectedNBATeams,
      filterState.selectedWNBATeams,
      filterState.selectedNBAGLeagueTeams,
      pageSize,
      1,
      filterCriteria,
      filterState.selectedCurrentlyWithATeam,
      filterState.selectedFormerCoach,
      filterState.internationalWorked,
      filterState.selectedExpType,
      filterState.selectedPExpereience,
      false
    )
  };


  const clearAllFilter = () => {
    handleServiceCall(
      "",
      [],
      [],
      [],
      [],
      selectSort,
      sortOrder,
      filterView,
      [],
      [],
      [],
      pageSize,
      currentPage,
      filterCriteria,
      [],
      [],
      false,
      [],
      [],
      false
    )
  };
  const sortOptions = role !== Role.Admin ? ["None", "Name", "Team"] : ["Name", "Team"];

  // const defaultProps = {
  //   options: sortOptions,
  //   getOptionLabel: (option) => option,
  // };

  const pageSizeOption = ["12", "24", "48", "72"];

  // const defaultPropsPage = {
  //   options: pageSizeOption,
  //   getOptionLabel: (option) => option,
  // };
  const handleProfileStatusChange = (userName, status, note, sendMail) => {
    let postData = { status: status, profileUserName: userName, reason: note, sendEmail: sendMail };
    setIsLoading(true);
    AdminService.changeProfileStatus(postData).then((data) => {
      // setFilterProfile([]);
      //setIsLoading(true);
      updateStatusProfile(data, currentPage);
    });
  };

  const handleChangeFilterCriteria = (e) => {
    //handleClearAllFilter();
    setCurrentPage(1);
    setFilterCriteria(e.target.value);
    setFilterProfile([]);
    setIsLoading(true);
  }

  function handleApproveClick() {
    if (openDialog.showApprove) {
      handleProfileStatusChange(openDialog.data.userName, AppConstants.PROFILE_STATUS.APPROVED)
      setOpenDialog({ open: true, data: openDialog.data, showApprove: false, showReject: true })
    }
    else if (openDialog.data.status.toLowerCase() === AppConstants.PROFILE_STATUS.APPROVED) {
      setOpenRejectDialog(true);
    }
  }
  const [openRejectDialog, setOpenRejectDialog] = useState(false);

  function btnRejectClick(note, sendMail) {
    setOpenRejectDialog(false);
    handleProfileStatusChange(openDialog.data.userName, AppConstants.PROFILE_STATUS.DENIED, note, sendMail)
    setOpenDialog({ open: true, data: openDialog.data, showApprove: true, showReject: false })
  }

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setFilterProfile([]);
    setIsLoading(true);
    setTabIndex(0);
    setFilterView(AppConstants.DASHBOARD_SECTION_ID.All);
  }

  const handleGroupPageSizeChange = (size) => {
    setGroupPageSize(size);
    setGroupPageNo(1);

  }

  function handleGroupClick() {
    setShowGroupDialog(true);
  }

  function handleShowcaseClick() {
    // if (selectedProfiles.length > AppConstants.MAX_PROFILE_ALLOWED_TO_SHOWCASE) {
    //   showAlert("error", "Please do not select more than " + AppConstants.MAX_PROFILE_ALLOWED_TO_SHOWCASE + " profiles to showcase");
    //   return false;
    // }
    // else {
    toggleParticipantDialog(true);
    setAction("Create");
    //}
  }

  function handleGroupClear() {
    if (!group)
      setShowGroupButton(false);
    setSelectedProfiles([]);
    setShowDownload(false);
    setShowShowcase(false);
  }

  async function handleDownLoadPDFClick() {
    //event.preventDefault();
    if (selectedProfiles && selectedProfiles.length > 0) {
      if (selectedProfiles.length > AppConstants.MAX_PROFILE_ALLOWED_TO_DOWNLOAD) {
        showAlert("error", "Please do not select more than " + AppConstants.MAX_PROFILE_ALLOWED_TO_DOWNLOAD + " profiles to download");
        return false;
      }

      setIsLoading(true);
      try {
        (async () => {
          await
            selectedProfiles.forEach((userName, ind) => {
              downloadPDF(userName, ind);

            });
        })();

      }
      catch {
        setIsLoading(false);
      }

      // var xhr = adminService.donwnloadMultiplePDF();
      // xhr.onload = function (e) {
      //   if (this.status == 200) {
      //     let fileName = "coachprofiles";
      //     var blob = new Blob([this.response], { type: "application/pdf" });
      //     var link = document.createElement("a");
      //     link.href = window.URL.createObjectURL(blob);
      //     link.download = fileName + ".pdf";
      //     link.click();
      //   }
      //   setIsLoading(false);
      // };
      // xhr.onerror = function (err) {
      //   console.log(err);
      //   setIsLoading(false);
      // }
      // xhr.send(JSON.stringify(selectedProfiles));
    }
  }

  async function downloadPDF(userName, ind) {
    var xhr = await coachProfileService.downloadPDF(userName);
    xhr.onload = function (e) {
      if (this.status == 200) {
        let fileName = userName;
        fileName = fileName.replace(/([^a-z0-9 _]+)/gi, '');
        var blob = new Blob([this.response], { type: "application/pdf" });
        var link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName + ".pdf";
        link.click();
      }
      if (ind == selectedProfiles.length - 1) {
        setTimeout(() => {
          setIsLoading(false);
        }, 5000);
      }
    };
    xhr.send();

  }


  const handleCloseClick = (isSuccess) => {
    toggleParticipantDialog(false);
  }
  const handleDeleteProfile = (email) => {
    adminService.deleteProfile(email)
      .then((response) => {
        if (response) {
          showAlert("success", 'Profile deleted successfully');
          handleSearchClick();
        }
        else
          showAlert("error", 'Cannot delete this profile.');
      })
      .catch((err) => {
        console.log(err);
      });
  }
  const handleSaveShowcaseClick = (showcaseData) => {
    if (showcaseData) {
      showcaseData.users = selectedProfiles;
      adminService.saveShowcase(showcaseData)
        .then(data => {
          if (typeof data == "string") {
            if (data == "duplicate")
              showAlert("error", "Showcase already exists.");
            else if (data == "invaliddate")
              showAlert("error", "Showcase already exists for selected date.");
            else
              showAlert("error", data);
          }
          else {
            if (showcaseData.Id)
              showAlert("success", 'Showcase updated successfully.');
            else
              showAlert("success", 'Showcase created successfully.');
            toggleParticipantDialog(false);
          }
        })
        .catch((err) => {
          showAlert("error", 'Error occured while saving.');
        });
    }
  }

  useEffect(() => {
    if (group) {
      getGroupTabData();
      if (!isGroupProfile) {
        setTabIndex(6);
        setIsGroupProfile(true);
      }
    }
    else {
      setTabIndex(0);
      setFilterView(AppConstants.DASHBOARD_SECTION_ID.All);
    }
  }, [group, groupPageSize]);

  useEffect(() => {
    if (groupPageNo > 1)
      getGroupTabData();
  }, [groupPageNo])


  function getGroupTabData() {
    if (group.id) {
      setIsLoading(true);
      setGroupUsers([]);
      setFilterProfile([]);
      setTotalRecords(0);
      setTotalRecordGroup(0);
      adminService.getUsersByGroupIdForTab(group.id, groupPageSize, groupPageNo)
        .then((response) => {
          setIsLoading(false);
          if (response.data.length > 0) {
            var users = response.data;
            setGroupUsers(users);
            setTotalRecordGroup(response.totalResults);
            let currentRole = userService.getUserRole();
            if (currentRole == Role.Admin) {
              setSelectedProfiles(users.map((u) => u.userName));
            }
          }
        })
        .catch((err) => {
          setIsLoading(false);
          console.log(err);
        });
    }

    setShowGroupTab(true);
    if (role == Role.Admin) {
      setSelectedProfiles(group.users);
      setShowDownload(false);
    }
    setShowGroupButton(true);
    setShowShowcase(false);
    setFilterView(AppConstants.DASHBOARD_SECTION_ID.GroupProfile);
  }


  const handleDiscardGroup = () => {
    setSelectedProfiles([]);
    setIsGroupProfile(false);
    setShowGroupDialog(false);
    setShowGroupButton(false);
    updateGroup(undefined);
    setTabIndex(0);
    setFilterView(AppConstants.DASHBOARD_SECTION_ID.All);
    setGroup(null);
    setCurrentPage(1);
    setShowGroupTab(false);
  }

  function saveGroup(name, showAsTAB, description) {
    if (name && name.trim().length > 0) {
      let userGroup = group ? group : {};
      userGroup.name = name;
      userGroup.description = description && description != null && description.trim();
      userGroup.showAsTAB = showAsTAB;
      userGroup.type = group ? group.type : selectedProfiles.length > 0 ? AppConstants.Static : AppConstants.Dynamic;
      userGroup.rules = selectedProfiles.length > 0 ? null : JSON.stringify(rules),
        userGroup.users = selectedProfiles,
        userGroup.isActive = 1
      AdminService.saveGroup(userGroup).then((data) => {
        if (!data) {
          showAlert("error", "Group already exists.");
        }
        else if (typeof data == "string") {
          showAlert("error", data);
        }
        else {
          showAlert("success", 'Group ' + (group ? "updated" : "created") + ' successfully.');
          setGroup(undefined);
          updateGroup(undefined);
          setShowGroupButton(false);
          setShowGroupDialog(false);
          setSelectedProfiles([]);
          setTimeout(() => {
            props.redirectToGroupList();
          }, 800);
        }
      });
    }
    else
      showAlert("error", "Please enter Group Name.");
  }
  function showAlert(sever, msg) {
    setShowError(true);
    setSeverity(sever)
    setMessage(msg);

  }
  const handleAlertClose = () => {
    setShowError(false);
    setSeverity("")
    setMessage("");
  }
  const hasAnyFilter = searchExecuted || !!(
    filterState.searchedText ||
    filterState.selectedTitle.length > 0 ||
    filterState.selectedLeague.length > 0 ||
    filterState.selectedChampionship.length > 0 ||
    filterState.selectedSkill.length > 0 ||
    filterState.selectedTeams.length > 0 ||
    filterState.selectedNBATeams.length > 0 ||
    filterState.selectedWNBATeams.length > 0 ||
    filterState.selectedNBAGLeagueTeams.length > 0 ||
    filterState.selectedCurrentlyWithATeam.length > 0 ||
    filterState.selectedFormerCoach.length > 0 ||
    filterState.internationalWorked ||
    filterState.selectedPExpereience.length > 0
  );

  const advancedFilterCount = filterState.useSmartSearch ? 0 : appliedFilterCount;

  return (
    <>
      <div className={clsx(classes.content, "custom-view")}>
        {!isGroupProfile && (
          <div className={classes.searchBar}>
            <TextField
              className={`${classes.searchInput} rotating-placeholder-input`}
              placeholder={rotatingPlaceholder}
              variant="outlined"
              size="medium"
              value={filterState.searchedText}
              onChange={(e) => dispatchFilter({ type: 'SET_FIELD', field: 'searchedText', value: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') handleMainSearchSubmit(); }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {filterState.searchedText && (
                      <IconButton size="small" onClick={() => {
                        dispatchFilter({ type: 'SET_FIELD', field: 'searchedText', value: '' });
                        setClearSearch(true);
                      }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Tooltip title="Search">
                      <IconButton size="small" color="primary" onClick={handleMainSearchSubmit}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={smartSearchEnabled ? "smart" : "advanced"}
              onChange={(e, val) => { if (val !== null) setSmartSearchEnabled(val === "smart"); }}
            >
              <ToggleButton value="smart">
                <Tooltip title="Smart Search" placement="bottom">
                  <span style={{ display: 'flex' }}><FlareIcon fontSize="small" /></span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="advanced">
                <Tooltip title="Advance Filters: filter by specific criteria" placement="bottom">
                  <span style={{ display: 'flex' }}><TuneIcon fontSize="small" /></span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            {!smartSearchEnabled && (
              <Tooltip title="Advanced Filters">
                <IconButton color={advancedFilterCount > 0 ? "primary" : "default"} onClick={() => setAdvancedFilterOpen(true)}>
                  <Badge badgeContent={advancedFilterCount} color="error" invisible={advancedFilterCount === 0}>
                    <TuneIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            {hasAnyFilter && (
              <Typography
                variant="body2"
                className={classes.clearAllLink}
                onClick={handleClearAllFilter}
              >
                Clear All
              </Typography>
            )}
          </div>
        )}
        {role == Role.Admin &&
          <AdminCoachesAppBar tabIndex={tabIndex} handleChangeAdmin={handleChangeAdmin} isXsDown={isXsDown} a11yProps={a11yProps} showUserPanel={showGroupTab} />}
        {(role == Role.FrontOffice || role == Role.FrontOfficePlusCoach) &&
          <FrontOfficeCoachesAppBar foTabs={foTabs} tabIndex={tabIndex} handleChange={handleChange} isXsDown={isXsDown} a11yProps={a11yProps} />}

        <div>
          <Toolbar className={classes.toolbar}>
            <div className={classes.sort}>
              {!isGroupProfile ?
                <>
                  <Box sx={{ display: {} }}>
                    <label>Sort by</label>
                  </Box>
                  <TextField
                    id="outlined-select-currency"
                    select
                    // label="Sort"
                    value={selectSort}
                    onChange={(event) => handleChangeSortBy(event)}
                    variant="standard"
                    size="small"
                    style={{ width: "11ch" }}
                    className={classes.sorting}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  >
                    {sortOptions.map((option) => (
                      <MenuItem key={option} value={option} style={theme.palette.mode === 'light' ? { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText } : {}}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  {selectSort !== 'None' &&
                    <IconButton
                      aria-label="card view"
                      color="primary"
                      title={sortOrder}
                      onClick={handleSortOrder}
                      style={{
                        padding: "4px 12px",
                        color: "text",
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary.main,
                        margin: "0 8px",
                        borderRadius: "12px",
                      }}
                    >
                      <>
                        <Typography variant="caption" size="small" style={{ fontWeight: "500", fontSize: ".93rem", color: "#ffffff" }} >
                          A-Z &nbsp;
                        </Typography><Avatar variant="square" className={classes.iconAvatar2} src={arrow_toggle_transparent}></Avatar></>
                    </IconButton>
                  }
                  <Box sx={{ display: {} }}>
                    <label className={classes.marginLeft}>Page Size</label>
                  </Box>
                  <TextField
                    id="outlined-select-currency"
                    select
                    // label="Page Size"
                    value={String(pageSize)}
                    onChange={(event) => handlePageSizeChange(parseInt(event.target.value))}
                    variant="standard"
                    size="small"
                    style={{ width: "10ch" }}
                    className={classes.Pagination}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  >
                    {pageSizeOption.map((option) => (
                      <MenuItem key={option} value={option} style={theme.palette.mode === 'light' ? { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText } : {}}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                </> :
                <>
                  <Box sx={{ display: {} }}>
                    <label className={classes.marginLeft}>Page Size</label>
                  </Box>
                  <TextField
                    id="outlined-select-currency"
                    select
                    // label="Page Size"
                    value={String(groupPageSize)}
                    onChange={(event) => handleGroupPageSizeChange(parseInt(event.target.value))}
                    variant="standard"
                    size="small"
                    style={{ width: "10ch" }}
                    className={classes.Pagination}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  >
                    {pageSizeOption.map((option) => (
                      <MenuItem key={option} value={option}  style={theme.palette.mode === 'light' ? { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText } : {}}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </>


              }
            </div>

            <Box component="div" sx={{display: 'flex', mr: 1}} className={classes.iconGroup}>
              <>
                {showDownload && selectedProfiles && selectedProfiles.length > 0 &&
                  <Tooltip title="Download Profiles">
                    <IconButton onClick={handleDownLoadPDFClick} aria-label="download">
                      <DownloadIcon style={{ color: theme.palette.text.primary }} />
                    </IconButton>
                  </Tooltip>
                }
                {(role == Role.Admin && ((selectedProfiles && selectedProfiles.length > 0) || showGroupButton)) &&
                  <Tooltip title={group ? "Update Group" : "Create Group"}>
                    <IconButton onClick={handleGroupClick} >
                      <CreateGroupIcon style={{ color: theme.palette.text.primary }}></CreateGroupIcon></IconButton>
                  </Tooltip>
                }
                {group && role == Role.Admin &&
                  <Tooltip title="Discard Group">
                    <IconButton onClick={handleDiscardGroup} color="secondary">
                      <DiscardGroupIcon style={{ color: theme.palette.text.primary }}></DiscardGroupIcon></IconButton>
                  </Tooltip>
                }
                {(role == Role.Admin && showShowcase && selectedProfiles && selectedProfiles.length > 0) &&
                  <Tooltip title="Create ShowCase">
                    <IconButton onClick={handleShowcaseClick}>
                      <CreateShowcaseIcon style={{ color: theme.palette.text.primary }}></CreateShowcaseIcon></IconButton>
                  </Tooltip>
                }
                {role == Role.Admin && selectedProfiles && selectedProfiles.length > 0 &&
                  <Tooltip title="Clear">
                    <IconButton onClick={handleGroupClear} color="secondary">
                      <ClearIcon style={{ color: theme.palette.text.primary }}></ClearIcon></IconButton>
                  </Tooltip>
                }
              </>
            </Box>
            <Box sx={{ display: {} }}>
              <Typography variant="subtitle2" className={classes.viewAs}>
                View as
              </Typography>
            </Box>
            <Tooltip title="List view">
              <IconButton
                aria-label="card view"
                edge="end"
                color={view === "list" ? "primary" : "default"}
                onClick={() => {
                  setView("list");
                  trackCustomEvents("ClickEvent", { event: "List View" });
                }}
              >
                <ViewListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Card view">
              <IconButton
                aria-label="card view"
                color={view === "card" ? "primary" : "default"}
                onClick={() => {
                  setView("card");
                  trackCustomEvents("ClickEvent", { event: "Card View" });
                }}
              >
                <ViewModuleIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </div>
        <Divider></Divider>

        {role == Role.Admin &&
          <AdminCoachesTabPanel
            theme={theme}
            tabIndex={tabIndex}
            handleChangeIndex={handleChangeIndex}
            currentUserName={currentUserName}
            handleOpenDialog={handleOpenDialogAdmin}
            profilesToDisplay={filteredProfileList}
            handleProfileStatusChange={handleProfileStatusChange}
            getLeagueTextByValue={getLeagueTextByValue}
            view={view}
            handleProfileChecked={handleProfileChecked}
            selectedProfiles={selectedProfiles}
            handleEditProfileClick={props.handleEditProfileClick}
            handleViewProfileClick={props.handleViewProfileClick}
            handleDeleteProfile={handleDeleteProfile}
            showUserPanel={showGroupTab}
            groupUsers={groupUsers}
          />}
        {(role == Role.FrontOffice || role == Role.FrontOfficePlusCoach || role == Role.Coach) &&
          <FrontOfficeCoachesTabPanel
            theme={theme}
            tabIndex={tabIndex}
            handleChangeIndex={handleChangeIndex}
            currentUserName={currentUserName}
            handleOpenDialog={handleOpenDialog}
            profilesToDisplay={filteredProfileList}
            handleBookMarked={handleBookMarked}
            getLeagueTextByValue={getLeagueTextByValue}
            view={view}
            handleViewProfileClick={props.handleViewProfileClick}
            handleProfileChecked={handleProfileChecked}
            selectedProfiles={selectedProfiles}
            foTabs={foTabs}
            groupUsers={groupUsers}
          />
        }

        {!isLoading && filteredProfileList.length === 0 && (hasAnyFilter || searchExecuted) && !isGroupProfile && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 330px)',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No result found
            </Typography>
          </Box>
        )}

        {filteredProfileList.length > 0 && !isGroupProfile && <Pagination
          pageSize={pageSize}
          totalProfile={totalRecords}
          changePageNumber={(e) => {
            onPageNoChange(e.selected + 1);
          }}
          currentPageNo={currentPage}
        />
        }
        {role != Role.Admin &&
          groupUsers && groupUsers.length > 0 && isGroupProfile && <Pagination
            pageSize={groupPageSize}
            totalProfile={totalRecordGroup}
            changePageNumber={(e) => {
              onGroupTabPageNoChange(e.selected + 1);
            }}
            currentPageNo={groupPageNo}
          />
        }

        {/* <ProfileDialogs
          handleEditProfileClick={props.handleEditProfileClick}
          open={openDialog.open}
          data={openDialog.data}
          showApprove={openDialog.showApprove}
          showReject={openDialog.showReject}
          onApproveClick={handleApproveClick}
          onRejectClick={() => setOpenRejectDialog(true)}
          jwtTokenDetails={jwtTokenDetails}
          onClose={() => setOpenDialog(false)}
          getLeagueTextByValue={getLeagueTextByValue}
        /> */}

        <RejectDialog
          open={openRejectDialog}
          onClose={() => setOpenRejectDialog(false)}
          onReject={(note, sendMail) => btnRejectClick(note, sendMail)}
        />
        {showGroupDialog && <CreateGroupDialog
          open={showGroupDialog}
          showFrontOfficeUser={true}
          onClose={() => setShowGroupDialog(false)}
          name={group ? group.name : ''}
          description={group ? group.description : ''}
          showAsTAB={group ? group.showAsTAB : ''}
          saveGroup={saveGroup}
        />
        }
        {role === Role.Admin && openParticipantDialog &&
          <AddShowcaseParticipantsDialog
            action={action}
            groupList={allGroups}
            open={openParticipantDialog}
            handleCloseClick={handleCloseClick}
            memoId={""}
            handleSaveShowcaseClick={handleSaveShowcaseClick}
            selectedProfiles={selectedProfiles}
          />}
        <AdvancedFilterDialog
          open={advancedFilterOpen}
          onClose={() => setAdvancedFilterOpen(false)}
          onApply={handleAdvancedFilterApply}
          onClearAll={handleClearAllFilter}
          filterState={filterState}
          onChecked={handleChecked}
          onChipDelete={handleChipDelete}
          filterTexts={filterTexts}
          onFilterTextChange={onWithinFilterSearchedTextchange}
          skills={skills}
          league={league}
          positionTitleList={positionTitleList}
          nbaTeamsList={nbaTeamsList}
          WnbaTeamsList={WnbaTeamsList}
          nbaGLeagueTeamsList={nbaGLeagueTeamsList}
          getLeagueTextByValue={getLeagueTextByValue}
        />
      </div>
      <Loader isLoading={isLoading} />
      <SnackbarMsg showError={showError} onClose={handleAlertClose} severity={severity} msg={message} />
    </>
  );
};

const CoachesManagementMainContainer = (props) => {
  const [coachProfileDialogOpen, setCoachProfileDialogOpen] = useState(false);
  const [selectedCoachUsername, setSelectedCoachUsername] = useState(null);
  const [dialogInitialPath, setDialogInitialPath] = useState(null);

  const handleEditProfileClick = (username) => {
    setSelectedCoachUsername(username);
    setDialogInitialPath(RouteConstants.ADMIN_COACH_PROFILE_EDIT + "/" + username);
    setCoachProfileDialogOpen(true);
  }

  const handleViewProfileClick = (username) => {
    setSelectedCoachUsername(username);
    setDialogInitialPath(null);
    setCoachProfileDialogOpen(true);
  }

  const redirectToGroupList = () => {
    props.history.push({
      pathname: RouteConstants.ADMIN_GROUP,
      state: {}
    });
  }

  return (
    <>
      <DashBoardProvider>
        <DashBoardConsumer>
          {(props) => {
            return <CoachesManagementContainer
              handleEditProfileClick={handleEditProfileClick}
              redirectToGroupList={redirectToGroupList}
              handleViewProfileClick={handleViewProfileClick}
            />;
          }}
        </DashBoardConsumer>
      </DashBoardProvider>
      <CoachProfileDialog
        open={coachProfileDialogOpen}
        username={selectedCoachUsername}
        initialPath={dialogInitialPath}
        onClose={() => setCoachProfileDialogOpen(false)}
      />
    </>
  );
};

export default CoachesManagementMainContainer;